import type {
  OpencodeMessagePart,
  OpencodePartData,
  SessionMessageWithParts,
} from "schemas/generated/opencode";

import React from "react";

import { ImageMessage } from "@agent-ui/features/chat/components/chat-elements/chat-message/ImageMessageItem";
import { MarkdownRenderer } from "@agent-ui/features/chat/components/chat-elements/chat-message/MarkdownRenderer";
import { CopyAction } from "@agent-ui/features/chat/components/chat-elements/message-list/message-actions/CopyAction";
import { DebugMenuAction } from "@agent-ui/features/chat/components/chat-elements/message-list/message-actions/DebugMenuAction";
import {
  Message,
  MessageActions,
  MessageContent,
} from "@agent-ui/features/chat/components/chat-elements/ui/Message";
import { VideoThumbnail } from "@agent-ui/features/chat/components/chat-elements/VideoThumbnail";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type FilePartData = Extract<OpencodePartData, { type: "file" }>;

const FilePartRenderer = ({ filePartData }: { filePartData: FilePartData }) => {
  if (filePartData.mime.startsWith("image/")) {
    return <ImageMessage src={filePartData.url} />;
  }
  if (filePartData.mime.startsWith("video/")) {
    return <VideoThumbnail src={filePartData.url} size="medium" />;
  }
  return null;
};

function NonFilePartRenderer({ partData }: { partData: OpencodePartData }) {
  return exhaustiveSwitch(partData)({
    text: (part) => <MarkdownRenderer>{part.text}</MarkdownRenderer>,
    reasoning: null,
    file: null,
    subtask: null,
    tool: null,
    "step-start": null,
    "step-finish": null,
    snapshot: null,
    patch: null,
    agent: null,
    retry: null,
    compaction: null,
  });
}

export default function UserMessage({
  messageWithParts,
}: {
  messageWithParts: SessionMessageWithParts;
}) {
  const { message, parts } = messageWithParts;

  const nonFileParts = parts.filter((part) => part.partData.type !== "file");
  const fileParts = parts.filter(
    (part): part is OpencodeMessagePart & { partData: FilePartData } => {
      return part.partData.type === "file";
    },
  );

  return (
    <Message key={message.id} from={message.role}>
      <div className="flex flex-row justify-end">
        {/* NOTE (Max, 2026-03-10): Rendering file parts separately so they can go above
        the grey user messsage buble (created by <MessageContent>) */}
        {fileParts.map((part) => (
          <FilePartRenderer key={part.id} filePartData={part.partData} />
        ))}
      </div>
      <MessageContent>
        {nonFileParts.map((part) => (
          <NonFilePartRenderer key={part.id} partData={part.partData} />
        ))}
      </MessageContent>

      <MessageActions>
        <CopyAction message={messageWithParts} />
        <DebugMenuAction message={messageWithParts} />
      </MessageActions>
    </Message>
  );
}
